import type { PipelineDoc } from './types';

/**
 * Audio-Pipeline (CUR-1571).
 *
 * Beschreibung gegen den Code gelesen: curalearn-backend/scripts/generate-audio.ts,
 * src/lib/lessonToSpeechScript.ts, src/lib/audioAlignment.ts.
 * Zahlen am 22.09.2026 aus den audio-manifest.json gezaehlt; Grundlinien aus
 * scripts/{readalong-gate,vertonung-drift}-baseline.json.
 */
export const audioPipeline: PipelineDoc = {
  slug: 'audio',
  title: 'Audio-Pipeline',
  claim: 'Vertonung als deterministische Funktion des Textes — der Hash des Sprechskripts entscheidet, ob überhaupt Kosten entstehen.',
  asOf: '2026-09-22',

  summary: [
    'Aus einer Lektion wird zuerst ein Sprechskript gerendert: derselbe Text erzeugt immer dasselbe Skript. Über dieses Skript läuft ein Hash, und dieser Hash ist das Skip-Kriterium. Unveränderter Text wird nicht neu vertont — nicht weil jemand daran denkt, sondern weil die Zahlen nicht übereinstimmen können.',
    'Damit ist die Kostenfrage konstruktiv gelöst statt durch Disziplin: ein Probelauf zur Kostenschätzung erzeugt Skripte und kostet nichts, weil vor dem ersten Zeichen an den Sprachdienst geprüft wird, ob sich etwas geändert hat.',
    'Was gesprochen wurde, ist nachprüfbar, ohne das MP3 anzuhören: das Sprechskript liegt als Textdatei im Repo, daneben der rohe Wortstrom mit Zeitmarken, und im Manifest steht der Hash. Ein Wächter hält die drei zusammen.',
  ],

  facts: [
    { label: 'Vertonte Lektionen', value: '305', hint: '300 Heilpraktiker · 5 Psychotherapie', source: 'gezählt: prisma/seed/content*/*/audio-manifest.json' },
    { label: 'Audiomaterial', value: '90,9 Stunden', hint: '327.304 Sekunden aus den Manifest-Dauern', source: 'Summe durationSec über alle Manifeste' },
    { label: 'Mit wortgenauem Mitlesen', value: '305 von 305', hint: 'jede vertonte Lektion hat ihr Alignment-Dokument', source: 'gezählt: alignmentUrl je Manifest-Eintrag' },
    { label: 'Gesprochene Zeichen', value: '4.081.645', hint: 'die Abrechnungsgröße des Sprachdienstes', source: 'Summe characterCount über alle Manifeste' },
    { label: 'Stimmen im Bestand', value: '1', hint: 'eine einzige Stimm-Kennung über alle 305 Lektionen', source: 'gezählt: voiceId je Manifest-Eintrag' },
    { label: 'Noch nicht vertont', value: '263 Lektionen', hint: '68 Psychotherapie · 195 Anthroposophie', source: 'Lektionen minus Manifest-Einträge je Baum' },
  ],

  stages: [
    {
      id: 'skript',
      nr: '1',
      title: 'Sprechskript rendern',
      purpose: 'Die Lektion in gesprochene Sprache übersetzen — deterministisch.',
      input: 'lessons.json',
      output: 'Sprechskript im Speicher, später als .txt-Beleg',
      tool: 'src/lib/lessonToSpeechScript.ts',
      detail: [
        'Dies ist der eigentliche Kern. Eine Tabelle wird nicht vorgelesen, sondern erzählt. Einheiten werden ausgeschrieben, Formeln in Sprache überführt, Abkürzungen bei ihrer ersten Nennung eingeführt. Pausen und Betonung kommen als Anweisungen ins Skript.',
        'Mitgeführt wird die Herkunft jedes Satzes: welcher Abschnitt der Lektion ihn erzeugt hat. Ohne diese Spur ließe sich später nicht sagen, welches gesprochene Wort auf welches geschriebene zeigt.',
        'Deterministisch heißt: gleiche Lektion, gleiches Skript, byteweise. Nur deshalb kann ein Hash die Kostenfrage entscheiden.',
      ],
      cost: 'kostenlos — kein Aufruf nach außen',
    },
    {
      id: 'hash',
      nr: '2',
      title: 'Hash bilden',
      purpose: 'Den Textstand in eine vergleichbare Zahl fassen.',
      input: 'Sprechskript',
      output: 'contentHash (SHA-256)',
      tool: 'sha256(script)',
      detail: [
        'Der Hash steht anschließend im Manifest neben der Audiodatei. Er beantwortet die Frage, die sonst niemand zuverlässig beantwortet: Ist dieses MP3 noch der Text, der heute in der App steht?',
      ],
      cost: 'kostenlos',
    },
    {
      id: 'skip',
      nr: '3',
      title: 'Kostengate',
      purpose: 'Abbrechen, bevor Geld fließt.',
      input: 'contentHash gegen Manifest',
      output: 'Entscheidung: vertonen oder überspringen',
      tool: '--dry-run · --skip-existing',
      detail: [
        'Stimmt der Hash mit dem Manifest überein, passiert nichts. Ein Probelauf über den gesamten Bestand ist damit gefahrlos und kostenlos — er sagt, was eine Vertonung kosten würde, ohne sie auszulösen.',
        'Hinter diesem Punkt beginnt der einzige Abschnitt der Strecke, der Geld kostet.',
      ],
      risk: 'Historisch stand das Schreiben der Belegskripte vor dieser Weiche. Siehe Lehren.',
    },
    {
      id: 'tts',
      nr: '4',
      title: 'Sprachsynthese',
      purpose: 'Das Skript sprechen lassen, mit Zeitmarke je Wort.',
      input: 'Sprechskript',
      output: 'Audio-Abschnitte + Wortstrom mit Zeitmarken',
      tool: 'ElevenLabs, abschnittsweise',
      detail: [
        'Aufgeteilt wird in Blöcke von rund 1.200 Zeichen, weil die Zeitmarken-Schnittstelle kleinere Einheiten braucht als die reine Sprachausgabe. Fehlversuche werden wiederholt, statt den Lauf abzubrechen.',
        'Zweitstimmen sind vorgesehen: Marker im Skript zerlegen es in Blöcke verschiedener Sprecher, etwa für eingestreute Patientenzitate.',
      ],
      cost: '0,15 USD je 1.000 Zeichen — rund 612 USD für den heutigen Bestand',
    },
    {
      id: 'loudness',
      nr: '5',
      title: 'Lautheit vereinheitlichen',
      purpose: 'Abschnittsgrenzen unhörbar machen.',
      input: 'Audio-Abschnitte',
      output: 'ein MP3 je Lektion',
      tool: 'ffmpeg, Zweipass-Normalisierung auf −16 LUFS',
      detail: [
        'Getrennt erzeugte Abschnitte haben leicht unterschiedliche Lautheit; ohne Ausgleich hört man jede Naht. Die Normalisierung auf −16 LUFS ist der Sprach-Standard, dazu eine Spitzenbegrenzung.',
        'Der durchgehende Neu-Encode behebt zugleich ein Abspielproblem in Safari, bei dem die Zeitleiste zusammengefügter Dateien einfror.',
      ],
    },
    {
      id: 'stille',
      nr: '6',
      title: 'Pausen messen',
      purpose: 'Verhindern, dass ein Wort in der Stille hervorgehoben wird.',
      input: 'fertiges MP3',
      output: 'Stille-Intervalle',
      tool: 'ffmpeg silencedetect',
      detail: [
        'Gemessen wird am fertigen MP3, nicht am Modell davor. Wörter, deren Zeitmarke in eine lange Pause fällt, werden herausgeschoben — sonst leuchtet beim Mitlesen ein Wort auf, während nichts zu hören ist.',
      ],
    },
    {
      id: 'alignment',
      nr: '7',
      title: 'Mitlese-Dokument bauen',
      purpose: 'Gesprochenes Wort auf geschriebenes Wort abbilden.',
      input: 'Wortstrom + Herkunftsspur aus Stufe 1',
      output: 'Alignment-Dokument',
      tool: 'src/lib/audioAlignment.ts',
      detail: [
        'Gebaut wird aus dem abgelegten Wortstrom, nicht aus einem erneuten Aufruf. Deshalb lässt sich das Mitlesen später byte-gleich neu erzeugen, ohne noch einmal zu vertonen — die Reparatur einer Mitlese-Störung kostet nichts.',
        'Erkannt werden auch entartete Zeitmarken: lange Läufe gleicher Werte, die der Sprachdienst gelegentlich liefert und die das Mitlesen unbrauchbar machen.',
      ],
    },
    {
      id: 'upload',
      nr: '8',
      title: 'Ablegen',
      purpose: 'Audio und Mitlese-Daten ausliefern.',
      input: 'MP3 + Alignment-Dokument',
      output: 'Objekte in Cloudflare R2',
      tool: 'src/lib/audioR2Key.ts',
      detail: [
        'Das MP3 liegt unter festem Schlüssel, das Mitlese-Dokument unter einem Schlüssel, der seinen eigenen Hash enthält. Damit kann ein neues Mitlese-Dokument ausgeliefert werden, ohne auf Zwischenspeicher zu warten — die alte Fassung bleibt gültig, bis die neue verwiesen wird.',
        'Die Schlüsselbildung liegt an einer Stelle. Jeder Schreibweg benutzt sie, damit nicht zwei Pfade dasselbe Objekt unterschiedlich benennen.',
      ],
    },
    {
      id: 'beleg',
      nr: '9',
      title: 'Beleg und Manifest schreiben',
      purpose: 'Nachprüfbar machen, was gesprochen wurde.',
      input: 'Skript, Wortstrom, Hashes',
      output: '.txt · .words.json · audio-manifest.json',
      tool: 'im Repo versioniert',
      detail: [
        'Die drei bewegen sich gemeinsam oder gar nicht. Wer wissen will, was in einer Lektion gesprochen wird, liest die Textdatei — er muss nicht 18 Minuten Audio anhören.',
        'Das Manifest trägt Adresse, Dauer, Stimme, Zeichenzahl, Erzeugungszeitpunkt und den Hash. Beim Import wandern diese Werte an die Lektion.',
      ],
    },
  ],

  gates: [
    {
      name: 'Beleg-Wächter',
      command: 'npm run tts:gate',
      promise: 'Der Hash der committeten Textdatei entspricht dem Hash im Manifest — der Beleg gehört zur Audiodatei',
      mode: 'gegen-null',
      current: '305 von 305 erfüllt',
      blindFor: 'ob das Skript fachlich richtig ist — nur die Zuordnung wird geprüft',
    },
    {
      name: 'Drift-Wächter',
      command: 'npm run vertonung:gate',
      promise: 'Das heute gerenderte Sprechskript entspricht dem, was vertont wurde — über jeden Abschnittstyp',
      mode: 'ratchet',
      current: 'Grundlinie 238 Lektionen (22.09.2026)',
      blindFor: 'nichts am Umfang — aber er behebt nichts, er hält den Rückstand nur fest',
    },
    {
      name: 'Mitlese-Wächter',
      command: 'npm run readalong:gate',
      promise: 'Keine neuen Mitlese-Störungen je Lektion — Wort-Marken und Typ-Verschiebungen',
      mode: 'ratchet',
      current: 'Grundlinie 8 Lektionen (22.09.2026)',
    },
    {
      name: 'Skript-Prüfung vor der Synthese',
      promise: 'Skriptlänge, eingeführte Abkürzungen, ausgeschriebene Einheiten, keine Auszeichnungsreste, Sprachmelodie',
      mode: 'manuell',
      current: 'agentisch, kein Skript',
      blindFor: 'bewusst nicht automatisiert — sie greift vor dem Geldfluss und soll dort ein Urteil verlangen',
    },
    {
      name: 'Untertitel-Prüfung',
      promise: 'Wortzahl je Mitlese-Marke plausibel',
      mode: 'test',
      blindFor: 'Zweitstimmen-Blöcke, Merkkarten und ersetzte Prosa — dort schaut sie nicht hin',
    },
  ],

  artifacts: [
    { name: 'Audiodatei', path: 'Cloudflare R2, nie im Repo', purpose: 'Das ausgelieferte MP3' },
    { name: 'Sprechskript', path: 'prisma/seed/tts-scripts*/{topic}/{lesson}.txt', purpose: 'Beweismittel: was gesprochen wurde, lesbar' },
    { name: 'Wortstrom', path: '{lesson}.words.json', purpose: 'Rohe Zeitmarken — erlaubt Reparatur ohne neue Vertonung' },
    { name: 'Manifest', path: 'prisma/seed/content*/{topic}/audio-manifest.json', purpose: 'Adresse, Dauer, Stimme, Zeichenzahl, Hash' },
    { name: 'Mitlese-Dokument', path: 'prisma/seed/content/{topic}/alignment/{lesson}.json + R2', purpose: 'Abbildung gesprochenes auf geschriebenes Wort' },
    { name: 'Aussprache-Lexikon', path: 'prisma/seed/audio-pronunciation-dict.json', purpose: 'Globale Aussprache-Vorgaben — siehe Rückstand' },
  ],

  costs: [
    { label: 'Preis', value: '0,15 USD je 1.000 Zeichen', source: 'scripts/generate-audio.ts, Kostenschätzung am Laufende' },
    { label: 'Bestand komplett', value: 'rund 612 USD', hint: '4.081.645 Zeichen zum genannten Satz', source: 'gerechnet aus den Manifest-Zeichenzahlen' },
    { label: 'Je Lektion', value: 'rund 2 USD', hint: 'im Mittel 13.382 Zeichen je Lektion', source: 'Zeichen geteilt durch 305 Lektionen' },
    { label: 'Erneute Vertonung ohne Textänderung', value: '0 USD', hint: 'Der Hash verhindert sie', source: 'Skip-Logik in scripts/generate-audio.ts' },
    { label: 'Mitlesen reparieren', value: '0 USD', hint: 'aus dem abgelegten Wortstrom, ohne neuen Aufruf', source: 'scripts/repair-readalong-timing.ts' },
  ],

  lessons: [
    {
      incident: 'Das Schreiben der Belegskripte stand vor der Skip-Weiche. Ein Probelauf, der nur die Kosten schätzen sollte, überschrieb dadurch 233 Belegskripte — und zerstörte genau die Belege, die beweisen sollten, was vertont worden war.',
      rule: 'Nur der Vertonungspfad schreibt Belege. Ein Lauf, der nichts vertont, darf nichts hinterlassen.',
      ticket: 'CUR-1198',
    },
    {
      incident: 'Nachträglich eingefügte Bilder verschoben die Abschnitts-Indizes. Das Mitlesen zeigte auf den falschen Absatz, obwohl Audio und Text einzeln in Ordnung waren.',
      rule: 'Das Mitlese-Dokument wird über seinen Inhalt adressiert und lässt sich ohne neue Vertonung reparieren. Ein Fehler in der Abbildung darf nicht den teuren Teil der Strecke erneut auslösen.',
    },
    {
      incident: 'Ein Gate, das den vollen Rückstand sofort meldete, stand ab dem ersten Tag rot. Ein solches Gate wird binnen einer Woche übergangen oder abgeschaltet.',
      rule: 'Gegen eine Grundlinie fahren, die Lektionen namentlich führt — nicht gegen eine Summe. Neue Defekte blocken, bekannte nicht. Summen ließen zu, dass ein behobener Defekt einen neuen deckt.',
    },
    {
      incident: 'Eine fachliche Korrektur an einer vertonten Lektion änderte den Text, ohne die Vertonung anzufassen. Der Drift-Wächter meldete es — die Korrektur selbst hatte es nicht bedacht.',
      rule: 'Text ändern heißt neu vertonen. Die Korrektur ist erst fertig, wenn Audio und Mitlesen nachgezogen sind.',
    },
  ],

  gaps: [
    {
      topic: 'Vertonungs-Drift',
      state: '238 von 305 Lektionen',
      why: 'Davon sind 211 reine Renderer-Drift: der Text ist wörtlich unverändert, nur die Regeln, nach denen gesprochen wird, haben sich verbessert. Nur 27 sind echte Textänderungen. Der Rückstand ist bewacht und wird in Wellen abgearbeitet — aber er ist da, und „alles grün" wäre gelogen.',
    },
    {
      topic: 'Aussprache-Lexikon',
      state: 'leer, obwohl der Mechanismus vollständig ist',
      why: 'Laden, Ersetzen, Kandidaten-Ernte und Testspur existieren. Nicht befüllt zu haben ist eine These, keine Lücke: die Aussprache wird über die Schreibweise im Skript gesteuert, nicht über ein Wörterbuch. Ein gepflegtes Lexikon zu behaupten, wäre falsch.',
    },
    {
      topic: 'Rohe Wortströme',
      state: 'nur für neuere Vertonungen',
      why: 'Erst seit CUR-1452 abgelegt. Ältere Vertonungen haben rekonstruierbare, aber nicht abgelegte Zeitmarken — ihre Mitlese-Reparatur ist aufwendiger.',
    },
    {
      topic: 'Öffentlicher Claim „über 100 Stunden"',
      state: 'nicht gedeckt',
      why: 'Der Zahlen-Wächter nimmt die Audiostunden ausdrücklich von der Prüfung aus. Gemessen aus den Manifesten sind es 90,9 Stunden. Diese Seite nennt die gemessene Zahl.',
    },
  ],

  sources: [
    { label: 'Audio-Pipeline, Vollfassung', path: 'docs/features/audio-pipeline-improvements.md' },
    { label: 'Synthese: Mitlese-Drift und Vertonungsqualität', path: 'docs/wiki/Data/spec-audio-pipeline-improvements.md' },
    { label: 'Das Sprechskript als Beweismittel', path: 'docs/wiki/Data/pipeline-sprechskript-als-beleg.md' },
    { label: 'Bestand und Wellenplan, mit Wächter-Tabelle', path: 'docs/wiki/Data/project-vertonung-bestand-wellen.md' },
    { label: 'Aussprache: fünf Schichten', path: 'docs/features/tts-aussprache-fachbegriffe.md' },
    { label: 'Was die Strecke am Text umbaut (generiert)', path: 'docs/features/tts-begriffs-katalog.md' },
  ],

  refresh: [
    { what: 'Vertonungsstand je Lektion', command: 'npx tsx scripts/audit-audio-coverage.ts' },
    { what: 'Drift-Grundlinie nachziehen', command: 'npx tsx scripts/audit-vertonung-drift.ts --update-baseline' },
    { what: 'Mitlese-Grundlinie nachziehen', command: 'npx tsx scripts/audit-readalong-gate-all.ts --update-baseline' },
    { what: 'Kosten schätzen, ohne zu vertonen', command: 'npm run generate-audio -- --all --dry-run' },
  ],
};
